/**
 * Main Quick Buy controller — delegated events.
 */
import { getConfig, getLabels } from './config.js';
import { emit } from './events/emitter.js';
import { createAbortable } from './utils/abort.js';
import { customerMessage } from './utils/errors.js';
import { fetchProductOptions, clearOptionsCache } from './api/product-options.js';
import { addSimpleProduct, addVariation } from './api/cart.js';
import { refreshCartFragments } from './integrations/cart-fragments.js';
import { requestStockAlert } from './integrations/stock-alerts.js';
import { createModal } from './modal/modal.js';

export function createController() {
	const modal = createModal();
	const abortable = createAbortable();
	let cardBusy = new WeakSet();

	function eventNode(event) {
		const node = event && event.target;
		if (!node) {
			return null;
		}
		if (typeof node.closest === 'function') {
			return node;
		}
		return node.parentElement || null;
	}

	function cardFromEvent(target) {
		if (!target || typeof target.closest !== 'function') {
			return null;
		}
		return target.closest('.wcqb');
	}

	function setTriggerLabel(btn, label) {
		if (!btn || !label) {
			return;
		}
		// Wishlist Buy Now keeps TI icon markup; only swap the text span.
		const txt = btn.querySelector('.tinvwl-txt');
		if (txt) {
			txt.textContent = label;
			return;
		}
		btn.textContent = label;
	}

	function idleTriggerLabel(btn, fallback) {
		return (btn && btn.getAttribute('data-idle-label')) || fallback;
	}

	function setCardState(card, state, label) {
		if (!card) {
			return;
		}
		const btn = card.querySelector('.wcqb__trigger');
		if (!btn) {
			return;
		}
		btn.classList.remove('is-adding', 'is-added', 'is-error');
		if (state) {
			btn.classList.add(state);
		}
		if (label) {
			setTriggerLabel(btn, label);
		}
		const busy = state === 'is-adding';
		if (btn.tagName === 'BUTTON') {
			btn.disabled = busy;
		} else {
			btn.setAttribute('aria-disabled', busy ? 'true' : 'false');
			btn.classList.toggle('disabled', busy);
		}
	}

	async function handleQuickAdd(card, trigger) {
		const productId = Number(card.getAttribute('data-product-id'));
		const context = card.getAttribute('data-context') || 'archive';
		const mode = card.getAttribute('data-mode') || 'variable';
		const productType = card.getAttribute('data-product-type') || '';
		const config = getConfig();
		const labels = getLabels();

		if (!productId || cardBusy.has(card)) {
			return;
		}

		if (mode === 'notify') {
			const variationId = Number(card.getAttribute('data-variation-id') || 0) || null;
			requestStockAlert(
				{
					productId,
					variationId,
					productName: card.getAttribute('data-product-name') || '',
					optionLabel: card.getAttribute('data-option-label') || card.getAttribute('data-pack-label') || '',
					context: context || 'wishlist',
				},
				trigger
			);
			return;
		}

		if (mode === 'simple' || productType === 'simple') {
			await addSimpleDirect(card, trigger, productId, context);
			return;
		}

		// Variable: fetch options first. Only open the modal when there are
		// multiple options (or a forced/unsupported/unavailable state). Single
		// purchasable variations add directly.
		cardBusy.add(card);
		setCardState(card, 'is-adding', labels.adding);

		const signal = abortable.start();
		try {
			const options = await fetchProductOptions(productId, context, signal);
			const purchasable = (options.variations || []).filter((v) => v.purchasable);
			const directSingle =
				(options.mode === 'variation' || options.mode === 'pack_size') &&
				purchasable.length === 1 &&
				!config.forcePopupSingle;

			if (directSingle) {
				cardBusy.delete(card);
				await addVariationDirect(card, trigger, productId, purchasable[0], context, options);
				return;
			}

			setCardState(card, '', idleTriggerLabel(trigger, labels.quickAdd || 'Quick Add'));
			cardBusy.delete(card);

			emit('wcQuickBuy:opened', { productId, context });
			modal.open(productId, context, trigger, options);

			if (options.mode === 'unsupported') {
				emit('wcQuickBuy:unsupportedProduct', {
					productId,
					context,
					productName: options.title || '',
				});
			}
		} catch (error) {
			if (error && error.name === 'AbortError') {
				return;
			}
			setCardState(card, 'is-error', idleTriggerLabel(trigger, labels.quickAdd || 'Quick Add'));
			cardBusy.delete(card);
			emit('wcQuickBuy:optionsFailed', {
				productId,
				context,
				errorCode: error.code || 'options_failed',
			});
			window.alert(customerMessage(error));
		}
	}

	async function addSimpleDirect(card, trigger, productId, context) {
		const labels = getLabels();
		const idle = idleTriggerLabel(trigger, labels.quickAdd || 'Quick Add');
		cardBusy.add(card);
		setCardState(card, 'is-adding', labels.adding);

		emit('wcQuickBuy:addStarted', { productId, context });

		try {
			const cart = await addSimpleProduct(productId, 1);
			setCardState(card, 'is-added', labels.added);
			emit('wcQuickBuy:addSucceeded', {
				productId,
				context,
				price: '',
				currency: getConfig().currency || '',
			});
			refreshCartFragments(cart, trigger);
			clearOptionsCache(productId);
			window.setTimeout(() => {
				setCardState(card, '', idle);
				cardBusy.delete(card);
			}, 1200);
		} catch (error) {
			setCardState(card, 'is-error', idle);
			cardBusy.delete(card);
			emit('wcQuickBuy:addFailed', {
				productId,
				context,
				errorCode: error.code || 'add_failed',
			});
			window.alert(customerMessage(error));
		}
	}

	async function addVariationDirect(card, trigger, productId, variation, context, options) {
		const labels = getLabels();
		const idle = idleTriggerLabel(trigger, labels.quickAdd || 'Quick Add');
		cardBusy.add(card);
		setCardState(card, 'is-adding', labels.adding);

		emit('wcQuickBuy:addStarted', {
			productId,
			variationId: variation.variation_id,
			optionLabel: variation.label,
			price: variation.raw_price,
			currency: options.currency && options.currency.code,
			context,
			productName: options.title || '',
		});

		try {
			const cart = await addVariation(variation.variation_id, variation.attributes, 1);
			const success = `${labels.added || 'Added'}: ${variation.label}`;
			setCardState(card, 'is-added', labels.added);
			emit('wcQuickBuy:addSucceeded', {
				productId,
				variationId: variation.variation_id,
				optionLabel: variation.label,
				price: variation.raw_price,
				currency: options.currency && options.currency.code,
				context,
				productName: options.title || '',
			});
			refreshCartFragments(cart, trigger);
			clearOptionsCache(productId);
			window.setTimeout(() => {
				setCardState(card, '', idle);
				cardBusy.delete(card);
			}, 1400);
			// Brief confirmation naming the pack.
			if (trigger) {
				trigger.setAttribute('aria-label', success);
			}
		} catch (error) {
			setCardState(card, 'is-error', idle);
			cardBusy.delete(card);
			emit('wcQuickBuy:addFailed', {
				productId,
				variationId: variation.variation_id,
				context,
				errorCode: error.code || 'add_failed',
			});
			window.alert(customerMessage(error));
		}
	}

	async function handleModalAdd() {
		const state = modal.getState();
		const labels = getLabels();
		const variation = modal.getSelectedVariation();

		if (!variation) {
			modal.showError(labels.chooseRequired || 'Please choose an option.');
			return;
		}

		if (state.adding) {
			return;
		}

		modal.setAdding(true);
		emit('wcQuickBuy:addStarted', {
			productId: state.productId,
			variationId: variation.variation_id,
			optionLabel: variation.label,
			price: variation.raw_price,
			currency: state.options && state.options.currency && state.options.currency.code,
			context: state.context,
			productName: state.options && state.options.title,
		});

		try {
			const cart = await addVariation(variation.variation_id, variation.attributes, 1);
			const message = `${labels.added || 'Added'}: ${variation.label}`;
			modal.showSuccess(message);
			emit('wcQuickBuy:addSucceeded', {
				productId: state.productId,
				variationId: variation.variation_id,
				optionLabel: variation.label,
				price: variation.raw_price,
				currency: state.options && state.options.currency && state.options.currency.code,
				context: state.context,
				productName: state.options && state.options.title,
			});
			refreshCartFragments(cart, state.trigger);
			clearOptionsCache(state.productId);
		} catch (error) {
			modal.showError(customerMessage(error));
			emit('wcQuickBuy:addFailed', {
				productId: state.productId,
				variationId: variation.variation_id,
				context: state.context,
				errorCode: error.code || 'add_failed',
			});
			clearOptionsCache(state.productId);
		}
	}

	function onClick(event) {
		// Wishlist carousel owns the shared modal while open.
		if (document.querySelector('.wcqb-modal.wcqb-wishlist-carousel-open')) {
			return;
		}

		const node = eventNode(event);
		if (!node) {
			return;
		}

		const target = node.closest(
			[
				'.wcqb__trigger',
				'[data-action="select-pack"]',
				'[data-action="add-to-basket"]',
				'[data-wcqb-close]',
				'[data-action="stock-alert"]',
				'[data-action="view-full"]',
				'.wcqb__view',
			].join(',')
		);

		if (!target) {
			return;
		}

		if (target.matches('.wcqb__view') || target.getAttribute('data-action') === 'view-full') {
			const card = cardFromEvent(target);
			emit('wcQuickBuy:viewProductClicked', {
				productId: card ? Number(card.getAttribute('data-product-id')) : null,
				context: card ? card.getAttribute('data-context') : 'archive',
			});
			return;
		}

		if (target.matches('.wcqb__trigger')) {
			const action = target.getAttribute('data-action');
			if (action === 'view') {
				return;
			}
			if (target.getAttribute('aria-disabled') === 'true') {
				event.preventDefault();
				return;
			}
			event.preventDefault();
			const card = cardFromEvent(target);
			if (card) {
				handleQuickAdd(card, target);
			}
			return;
		}

		if (target.hasAttribute('data-wcqb-close')) {
			const closeType = target.getAttribute('data-wcqb-close');
			if (closeType === 'backdrop' && !getConfig().closeOnBackdrop) {
				return;
			}
			event.preventDefault();
			modal.close();
			return;
		}

		if (target.getAttribute('data-action') === 'select-pack') {
			event.preventDefault();
			const variationId = Number(target.getAttribute('data-variation-id'));
			modal.selectVariation(variationId);
			const selected = modal.getSelectedVariation();
			const state = modal.getState();
			emit('wcQuickBuy:variationSelected', {
				productId: state.productId,
				variationId,
				optionLabel: selected && selected.label,
				price: selected && selected.raw_price,
				currency: state.options && state.options.currency && state.options.currency.code,
				context: state.context,
				productName: state.options && state.options.title,
			});
			return;
		}

		if (target.getAttribute('data-action') === 'add-to-basket') {
			event.preventDefault();
			handleModalAdd();
			return;
		}

		if (target.getAttribute('data-action') === 'stock-alert') {
			event.preventDefault();
			const state = modal.getState();
			const variationId = target.getAttribute('data-variation-id');
			const variation = (state.options && state.options.variations || []).find(
				(v) => String(v.variation_id) === String(variationId)
			);
			requestStockAlert(
				{
					productId: state.productId,
					variationId: variationId ? Number(variationId) : null,
					productName: state.options && state.options.title,
					optionLabel: variation ? variation.label : '',
					context: state.context,
				},
				target
			);
		}
	}

	function bind() {
		document.addEventListener('click', onClick);
		document.addEventListener('keydown', (event) => modal.onKeydown(event));
		modal.ensure();
	}

	return {
		bind,
		modal,
		clearOptionsCache,
	};
}
